import { motion } from "framer-motion";
import { Phone } from "lucide-react";
import { WhatsAppIcon } from "./BrandIcons";

export const AgentFloat = ({ src, alt, delay = 0, className = "", size = 200 }) => (
    <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        initial={{ opacity: 0, y: 24, scale: 0.92 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
        className={`animate-floaty object-contain ${className}`}
        style={{
            maxWidth: size,
            maxHeight: "100%",
            width: "auto",
            height: "auto",
            animationDelay: `${delay + 0.5}s`,
            filter: "drop-shadow(0 24px 32px rgba(2,6,17,0.6))",
        }}
    />
);

const MiniWave = () => (
    <span className="flex items-end gap-[2px] h-3" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
            <span key={i} className="wave-bar w-[2px] rounded-full bg-cyan-300" style={{ height: "100%", animationDelay: `${i * 0.12}s` }} />
        ))}
    </span>
);

const TONE_CLS = {
    blue: "bg-cyan-500/20 text-cyan-300",
    green: "bg-emerald-500/20 text-emerald-300",
    cyan: "bg-sky-500/20 text-sky-300",
    amber: "bg-amber-500/20 text-amber-300",
    teal: "bg-teal-500/20 text-teal-300",
};

export const AgentNote = ({ tone = "blue", title, sub, delay = 0, className = "", testid, icon }) => (
    <motion.div
        data-testid={testid}
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay, duration: 0.5 }}
        className={`glass flex items-center gap-2 rounded-xl px-3 py-1.5 ${className}`}
    >
        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${TONE_CLS[tone] || TONE_CLS.blue}`}>
            {icon || (tone === "blue" ? <Phone className="h-3 w-3" /> : <WhatsAppIcon className="h-3.5 w-3.5" color="#34D399" />)}
        </span>
        <span className="min-w-0">
            <span className="block whitespace-nowrap text-[10px] font-bold leading-tight text-white">{title}</span>
            {sub && <span className="block whitespace-nowrap text-[8px] leading-tight text-slate-400 notranslate">{sub}</span>}
        </span>
        {tone === "blue" && <MiniWave />}
    </motion.div>
);

const AgentCard = ({ src, alt, note, tone, compact, testid }) => (
    <div className="flex h-full w-full flex-col items-center justify-between gap-1 p-2" data-testid={testid}>
        <div className="flex min-h-0 w-full flex-1 items-center justify-center">
            <AgentFloat src={src} alt={alt} size={compact ? 130 : 165} />
        </div>
        {note && <AgentNote tone={tone} title={note.title} sub={note.sub} testid={`${testid}-note`} />}
    </div>
);

/* Veer — AI Calling Agent (blue, headset + phone) */
export const VeerVisual = ({ compact = false }) => (
    <AgentCard
        src="/assets/veer.png"
        alt="Veer — AI Calling Agent"
        compact={compact}
        testid="visual-calling"
        tone="blue"
        note={{ title: "Veer is on it.", sub: "Calling, with a personal touch" }}
    />
);

/* Sana — AI WhatsApp Agent (green, chat) */
export const SanaVisual = ({ compact = false }) => (
    <AgentCard
        src="/assets/sana.png"
        alt="Sana — AI WhatsApp Agent"
        compact={compact}
        testid="visual-whatsapp"
        tone="green"
        note={{ title: "Consider it answered.", sub: "Sana · WhatsApp AI" }}
    />
);

/* Support — cyan robot with ticket panel */
export const SupportVisual = ({ compact = false }) => (
    <AgentCard
        src="/assets/robot-support.png"
        alt="AI Customer Support Agent"
        compact={compact}
        testid="visual-support"
        tone="cyan"
        note={{ title: "Solved in seconds.", sub: "24×7 AI Support" }}
    />
);

/* Lead Generation — amber robot */
export const LeadGenVisual = ({ compact = false }) => (
    <AgentCard
        src="/assets/robot-leadgen.png"
        alt="AI Lead Generation Agent"
        compact={compact}
        testid="visual-leadgen"
        tone="amber"
        note={{ title: "Every lead captured.", sub: "Qualified & scored" }}
    />
);

/* Workflow Automation — teal robot */
export const AutomationVisual = ({ compact = false }) => (
    <AgentCard
        src="/assets/robot-automation.png"
        alt="AI Workflow Automation Agent"
        compact={compact}
        testid="visual-automation"
        tone="teal"
        note={{ title: "Runs on autopilot.", sub: "Follow-ups & bookings" }}
    />
);
